import React, { useState, useEffect, useCallback } from 'react';
import { Routes, Route, NavLink, useNavigate } from 'react-router-dom';
import { FiSettings, FiBarChart2, FiGift, FiFileText, FiLogOut, FiVolume2, FiCalendar, FiClock, FiEdit2, FiCheck, FiX, FiPower, FiLock, FiBook, FiAward } from 'react-icons/fi';
import api from '../services/api';
import AdminPrizeManagementPage from './AdminPrizeManagementPage';
import AdminSpinRecordPage from './AdminSpinRecordPage';
import AdminFootballPage from './AdminFootballPage';
import './Admin.css';

function AdminDashboard() {
  const [campaigns, setCampaigns] = useState([]);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const navigate = useNavigate();

  const loadCampaigns = useCallback(async () => {
    try {
      const data = await api.getCampaigns();
      setCampaigns(data);
      setSelectedCampaign(prev => {
        if (!prev) return data.length > 0 ? data[0] : null;
        const updated = data.find(c => c.id === prev.id);
        return updated || (data.length > 0 ? data[0] : null);
      });
    } catch (err) {
      if (err.status === 401) {
        navigate('/admin/login');
      }
    }
  }, [navigate]);

  useEffect(() => {
    const token = localStorage.getItem('adminToken');
    if (!token) {
      navigate('/admin/login');
      return;
    }
    loadCampaigns();
  }, [loadCampaigns, navigate]);

  const handleLogout = () => {
    api.clearAdminToken();
    navigate('/admin/login');
  };

  return (
    <div className="admin-layout">
      <aside className="admin-sidebar">
        <div className="admin-sidebar-header">
          <h2><FiSettings style={{ marginRight: 8, verticalAlign: 'middle' }} />後台管理</h2>
          <span className="admin-version">v1.0</span>
        </div>

        {/* 活動選擇 */}
        <div className="admin-campaign-select">
          <label>選擇活動</label>
          <select
            value={selectedCampaign?.id || ''}
            onChange={e => {
              const c = campaigns.find(c => c.id === parseInt(e.target.value));
              setSelectedCampaign(c);
            }}
          >
            {campaigns.map(c => (
              <option key={c.id} value={c.id}>{c.title}</option>
            ))}
          </select>
        </div>

        <nav className="admin-nav">
          <NavLink to="/admin" end className="admin-nav-link">
            <FiBarChart2 style={{ marginRight: 8, verticalAlign: 'middle' }} />儀表板
          </NavLink>
          <NavLink to="/admin/prizes" className="admin-nav-link">
            <FiGift style={{ marginRight: 8, verticalAlign: 'middle' }} />獎項管理
          </NavLink>
          <NavLink to="/admin/records" className="admin-nav-link">
            <FiFileText style={{ marginRight: 8, verticalAlign: 'middle' }} />抽獎紀錄
          </NavLink>
          <NavLink to="/admin/terms" className="admin-nav-link">
            <FiBook style={{ marginRight: 8, verticalAlign: 'middle' }} />活動條款
          </NavLink>
          <NavLink to="/admin/football" className="admin-nav-link">
            <FiAward style={{ marginRight: 8, verticalAlign: 'middle' }} />足球預測
          </NavLink>
          <NavLink to="/admin/password" className="admin-nav-link">
            <FiLock style={{ marginRight: 8, verticalAlign: 'middle' }} />密碼變更
          </NavLink>
        </nav>

        <button className="admin-logout-btn" onClick={handleLogout}>
          <FiLogOut style={{ marginRight: 6, verticalAlign: 'middle' }} />登出
        </button>
      </aside>

      <main className="admin-main">
        <Routes>
          <Route index element={
            <AdminOverview campaign={selectedCampaign} onUpdate={loadCampaigns} />
          } />
          <Route path="prizes" element={
            <AdminPrizeManagementPage campaignId={selectedCampaign?.id} />
          } />
          <Route path="records" element={
            <AdminSpinRecordPage campaignId={selectedCampaign?.id} />
          } />
          <Route path="terms" element={
            <AdminTermsPage campaignId={selectedCampaign?.id} />
          } />
          <Route path="football" element={
            <AdminFootballPage />
          } />
          <Route path="password" element={
            <AdminChangePasswordPage />
          } />
        </Routes>
      </main>
    </div>
  );
}

/**
 * 後台總覽
 */
function AdminOverview({ campaign, onUpdate }) {
  const [editingTitle, setEditingTitle] = useState(false);
  const [titleValue, setTitleValue] = useState('');
  const [saving, setSaving] = useState(false);
  const [statusSaving, setStatusSaving] = useState(false);

  if (!campaign) return <div className="admin-empty">請選擇活動</div>;

  const handleEditTitle = () => {
    setTitleValue(campaign.title);
    setEditingTitle(true);
  };

  const handleSaveTitle = async () => {
    if (!titleValue.trim()) return;
    setSaving(true);
    try {
      await api.updateCampaign(campaign.id, { title: titleValue.trim() });
      setEditingTitle(false);
      if (onUpdate) onUpdate();
    } catch (err) {
      alert(err.message || '更新失敗');
    } finally {
      setSaving(false);
    }
  };

  const handleCancelTitle = () => {
    setEditingTitle(false);
  };

  const handleStatusChange = async (newStatus) => {
    if (newStatus === campaign.status) return;
    setStatusSaving(true);
    try {
      await api.updateCampaign(campaign.id, { status: newStatus });
      if (onUpdate) onUpdate();
    } catch (err) {
      alert(err.message || '更新狀態失敗');
    } finally {
      setStatusSaving(false);
    }
  };

  return (
    <div className="admin-overview">
      <h1 className="admin-page-title">活動總覽</h1>
      <div className="admin-stats-grid">
        <div className="admin-stat-card">
          <div className="stat-icon"><FiVolume2 size={24} /></div>
          <div className="stat-label">活動名稱</div>
          {editingTitle ? (
            <div className="stat-value-edit">
              <input
                type="text"
                value={titleValue}
                onChange={e => setTitleValue(e.target.value)}
                className="stat-edit-input"
                disabled={saving}
                autoFocus
                onKeyDown={e => { if (e.key === 'Enter') handleSaveTitle(); if (e.key === 'Escape') handleCancelTitle(); }}
              />
              <div className="stat-edit-actions">
                <button className="stat-edit-btn save" onClick={handleSaveTitle} disabled={saving}><FiCheck size={16} /></button>
                <button className="stat-edit-btn cancel" onClick={handleCancelTitle} disabled={saving}><FiX size={16} /></button>
              </div>
            </div>
          ) : (
            <div className="stat-value stat-value-editable" onClick={handleEditTitle}>
              {campaign.title}
              <FiEdit2 size={14} className="edit-icon" />
            </div>
          )}
        </div>
        <div className="admin-stat-card">
          <div className="stat-icon"><FiPower size={24} /></div>
          <div className="stat-label">活動狀態</div>
          <div className="status-toggle-group">
            {[
              { value: 'active', label: '進行中' },
              { value: 'paused', label: '暫停' },
              { value: 'ended', label: '已結束' },
            ].map(opt => (
              <button
                key={opt.value}
                className={`status-toggle-btn status-${opt.value}${campaign.status === opt.value ? ' active' : ''}`}
                onClick={() => handleStatusChange(opt.value)}
                disabled={statusSaving}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>
        <div className="admin-stat-card">
          <div className="stat-icon"><FiClock size={24} /></div>
          <div className="stat-label">開始時間</div>
          <div className="stat-value stat-small">
            {new Date(campaign.start_at).toLocaleString('zh-TW')}
          </div>
        </div>
        <div className="admin-stat-card">
          <div className="stat-icon"><FiClock size={24} /></div>
          <div className="stat-label">結束時間</div>
          <div className="stat-value stat-small">
            {new Date(campaign.end_at).toLocaleString('zh-TW')}
          </div>
        </div>
      </div>
    </div>
  );
}

const DEFAULT_TERMS = `一、活動辦法
1. 本活動由主辦單位舉辦，參加者須填寫真實個人資料方可參與抽獎。
2. 每人限參加一次，重複報名以首次為準。
3. 中獎者需依主辦單位通知辦理領獎事宜。

二、個人資料蒐集與使用聲明
1. 依據個人資料保護法，主辦單位將於本活動目的範圍內蒐集、處理及利用您的個人資料。
2. 蒐集之個人資料包含：姓名、身分證字號及手機號碼。
3. 資料使用目的：身分驗證、中獎通知、獎品發放及活動相關聯繫。
4. 您的個資將妥善保管，不提供予無關之第三方。
5. 活動結束後六個月內，將依法刪除或停止利用您的個人資料。

三、注意事項
1. 主辦單位保留隨時修改、暫停或終止活動之權利。
2. 如有疑似不當行為，主辦單位有權取消參加資格。
3. 本活動相關問題，請洽主辦單位客服。`;

/**
 * 活動條款管理頁面
 */
function AdminTermsPage({ campaignId }) {
  const [terms, setTerms] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');

  useEffect(() => {
    if (!campaignId) return;
    setLoading(true);
    api.getTerms(campaignId).then(data => {
      setTerms(data.terms || '');
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [campaignId]);

  const handleSave = async () => {
    setSaving(true);
    setMsg('');
    try {
      await api.updateTerms(campaignId, terms);
      setMsg('已儲存');
      setTimeout(() => setMsg(''), 2000);
    } catch (err) {
      setMsg(err.message || '儲存失敗');
    } finally {
      setSaving(false);
    }
  };

  const handleLoadTemplate = () => {
    if (terms.trim() && !window.confirm('將覆蓋目前內容，確定要載入預設範本？')) return;
    setTerms(DEFAULT_TERMS);
  };

  if (!campaignId) return <div className="admin-empty">請先選擇活動</div>;

  return (
    <div className="admin-page-content">
      <h1 className="admin-page-title"><FiBook style={{ marginRight: 8, verticalAlign: 'middle' }} />活動條款與個資使用聲明</h1>
      {loading ? (
        <div style={{ color: '#888', padding: 20 }}>載入中...</div>
      ) : (
        <div className="terms-editor-wrap">
          <div className="terms-editor-toolbar">
            <button className="admin-btn secondary" onClick={handleLoadTemplate}>
              載入預設範本
            </button>
            <button className="admin-btn primary" onClick={handleSave} disabled={saving}>
              {saving ? '儲存中...' : '儲存'}
            </button>
            {msg && <span className="terms-msg">{msg}</span>}
          </div>
          <textarea
            className="terms-textarea"
            value={terms}
            onChange={e => setTerms(e.target.value)}
            placeholder="請輸入活動條款內容..."
            rows={20}
          />
          <div className="terms-preview-label">前台預覽</div>
          <div className="terms-preview">
            {terms ? terms.split('\n').map((line, i) => (
              <p key={i}>{line || <br/>}</p>
            )) : <span style={{ color: '#666' }}>尚未填寫條款</span>}
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * 密碼變更頁面
 */
function AdminChangePasswordPage() {
  const [form, setForm] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState({ type: '', text: '' });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMsg({ type: '', text: '' });

    if (!form.currentPassword || !form.newPassword) {
      setMsg({ type: 'error', text: '請填寫所有欄位' });
      return;
    }
    if (form.newPassword.length < 6) {
      setMsg({ type: 'error', text: '新密碼至少 6 個字元' });
      return;
    }
    if (form.newPassword !== form.confirmPassword) {
      setMsg({ type: 'error', text: '兩次密碼不一致' });
      return;
    }

    setSaving(true);
    try {
      await api.changePassword(form.currentPassword, form.newPassword);
      setMsg({ type: 'success', text: '密碼已更新成功' });
      setForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (err) {
      setMsg({ type: 'error', text: err.message || '密碼更新失敗' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="admin-page-content">
      <h1 className="admin-page-title"><FiLock style={{ marginRight: 8, verticalAlign: 'middle' }} />變更密碼</h1>
      <form className="password-form" onSubmit={handleSubmit}>
        {msg.text && (
          <div className={`password-msg ${msg.type}`}>{msg.text}</div>
        )}
        <div className="password-field">
          <label>目前密碼</label>
          <input
            type="password"
            value={form.currentPassword}
            onChange={e => setForm(f => ({ ...f, currentPassword: e.target.value }))}
            placeholder="請輸入目前密碼"
            autoComplete="current-password"
          />
        </div>
        <div className="password-field">
          <label>新密碼</label>
          <input
            type="password"
            value={form.newPassword}
            onChange={e => setForm(f => ({ ...f, newPassword: e.target.value }))}
            placeholder="至少 6 個字元"
            autoComplete="new-password"
          />
        </div>
        <div className="password-field">
          <label>確認新密碼</label>
          <input
            type="password"
            value={form.confirmPassword}
            onChange={e => setForm(f => ({ ...f, confirmPassword: e.target.value }))}
            placeholder="再次輸入新密碼"
            autoComplete="new-password"
          />
        </div>
        <button type="submit" className="admin-btn primary" disabled={saving}>
          {saving ? '更新中...' : '更新密碼'}
        </button>
      </form>
    </div>
  );
}

export default AdminDashboard;
